<template>
  <app-page labelledby="page-title">
    <page-header title="Профиль организации" description="Реквизиты, юридический адрес и визуальное оформление организации.">
      <template #status>
        <status-badge :label="canEditOrg ? 'Можно редактировать' : 'Только просмотр'" :tone="canEditOrg ? 'success' : 'neutral'" :icon="canEditOrg ? 'edit' : 'visibility'" />
      </template>
    </page-header>

    <app-state v-if="state !== 'ready'" :state="state" :title="stateTitle" :message="stateMessage" @retry="requestData" />
    <template v-else>
      <page-section title="Организация" description="Основные сведения для документов и отчетности.">
        <div class="identity">
          <q-avatar size="6rem" color="primary" text-color="white">
            <img v-if="organisation.avatar_url" :src="avatarSrc(organisation.avatar_url)" alt="Аватар организации">
            <span v-else>{{ orgInitial }}</span>
          </q-avatar>
          <div class="identity__content">
            <strong>{{ organisation.name || 'Организация' }}</strong>
            <span>ИНН {{ organisation.inn || 'не указан' }}</span>
            <div v-if="canEditOrg" class="identity__actions">
              <q-file ref="orgAvatarInput" v-model="newAvatar" class="hidden" accept="image/png,image/jpeg" @update:model-value="onAvatarSelected" />
              <q-btn flat no-caps icon="photo_camera" label="Заменить изображение" :loading="avatarLoading" @click="$refs.orgAvatarInput.pickFiles()" />
              <q-btn v-if="organisation.avatar_url" flat no-caps color="negative" label="Удалить" :loading="avatarLoading" @click="deleteAvatar" />
            </div>
          </div>
        </div>
        <p v-if="avatarError" role="alert" class="form-error">{{ avatarError }}</p>
      </page-section>

      <page-section title="Реквизиты" description="Сведения получены из карточки организации и используются без сокращений.">
        <dl class="requisites">
          <div v-for="field in requisiteFields" :key="field.label">
            <dt>{{ field.label }}</dt>
            <dd>{{ field.value || 'Не указано' }}</dd>
          </div>
        </dl>
      </page-section>

      <page-section title="Адрес и контакты" description="Проверьте данные перед формированием юридически значимых документов.">
        <dl class="requisites requisites--single">
          <div><dt>Юридический адрес</dt><dd>{{ organisation.address || 'Не указан' }}</dd></div>
          <div><dt>Телефон</dt><dd>{{ organisation.phone || 'Не указан' }}</dd></div>
          <div><dt>Email</dt><dd>{{ organisation.email || 'Не указан' }}</dd></div>
        </dl>
        <p v-if="!canEditOrg" class="readonly-note">Для изменения данных нужны права управления организацией.</p>
      </page-section>
    </template>
  </app-page>
</template>

<script>
import axios from 'axios'
import { apiUrl, mediaUrl } from '../../../config'
import AppPage from '../../../components/ui/AppPage.vue'
import AppState from '../../../components/ui/AppState.vue'
import PageHeader from '../../../components/ui/PageHeader.vue'
import PageSection from '../../../components/ui/PageSection.vue'
import StatusBadge from '../../../components/ui/StatusBadge.vue'

export default {
  name: 'OrganizationProfile',
  components: { AppPage, AppState, PageHeader, PageSection, StatusBadge },
  data() {
    return {
      state: 'loading', stateTitle: '', stateMessage: '', infoLoaded: false, permissionLoaded: false,
      canEditOrg: false, newAvatar: null, avatarLoading: false, avatarError: '',
      organisation: { name: '', inn: '', kpp: '', address: '', ogrn: '', okpo: '', okved: '', phone: '', email: '', avatar_url: null }
    }
  },
  computed: {
    orgInitial() { return (this.organisation.name || '?').slice(0, 1).toUpperCase() },
    requisiteFields() {
      return [
        { label: 'Полное наименование', value: this.organisation.name },
        { label: 'ИНН', value: this.organisation.inn },
        { label: 'КПП', value: this.organisation.kpp },
        { label: 'ОГРН', value: this.organisation.ogrn },
        { label: 'ОКПО', value: this.organisation.okpo },
        { label: 'ОКВЭД', value: this.organisation.okved }
      ]
    }
  },
  created() {
    this.onInfo = (response) => {
      if (response.error) { this.showError('Не удалось загрузить профиль организации'); return }
      const meta = response.organisation_metadata || response
      const full = meta.full_data || {}
      this.organisation = {
        name: meta.display_name || meta.name || full.name?.full_with_opf || '',
        inn: meta.inn || full.inn || '', kpp: meta.kpp || full.kpp || '', ogrn: meta.ogrn || full.ogrn || '',
        okpo: meta.okpo || full.okpo || '', okved: meta.okved || full.okved || '',
        address: meta.address || meta.adress || full.address?.unrestricted_value || full.address?.value || '',
        phone: meta.phone || full.phone || '', email: meta.email || full.email || '', avatar_url: meta.avatar_url || null
      }
      this.infoLoaded = true; this.finishLoading()
    }
    this.onUser = (response) => { this.canEditOrg = response.user?.role?.permissions?.edit_org === true; this.permissionLoaded = true; this.finishLoading() }
    this.emitter.on('organisationInfoWasReceived', this.onInfo)
    this.emitter.on('didReceoveUserInfoAcrossOrg', this.onUser)
    if (this.connection.isAuthenticated) this.requestData()
    else this.emitter.on('connectionReady', this.requestData)
  },
  beforeUnmount() {
    this.emitter.off('organisationInfoWasReceived', this.onInfo)
    this.emitter.off('didReceoveUserInfoAcrossOrg', this.onUser)
    this.emitter.off('connectionReady', this.requestData)
  },
  methods: {
    requestData() {
      this.state = 'loading'; this.infoLoaded = false; this.permissionLoaded = false
      this.connection.getOrganisationInfo(this.connection.makeRequestId('org-info'))
      this.connection.getUserInfoAcrossOrg(this.connection.makeRequestId('org-permission'))
    },
    finishLoading() { if (this.infoLoaded && this.permissionLoaded && this.state !== 'error') this.state = 'ready' },
    showError(title) { this.state = 'error'; this.stateTitle = title; this.stateMessage = 'Проверьте подключение и повторите запрос.' },
    avatarSrc(url) { return /^https?:|^data:/.test(url || '') ? url : `${mediaUrl}${url.startsWith('/') ? url : `/${url}`}` },
    validateAvatar(file) {
      if (!['image/png', 'image/jpeg'].includes(file?.type)) return 'Можно загрузить только PNG или JPG.'
      if (file.size > 5 * 1024 * 1024) return 'Размер изображения не должен превышать 5 МБ.'
      return ''
    },
    authPayload() {
      if (!this.connection.token || !this.connection.token_uuid) this.connection.loadCredentials(this.$q)
      return { token: this.connection.token || '', token_uuid: this.connection.token_uuid || '' }
    },
    async onAvatarSelected(file) {
      if (!file) return
      this.avatarError = this.validateAvatar(file)
      if (this.avatarError) { this.newAvatar = null; return }
      const form = new FormData(); form.append('file', file); form.append('org_id', this.connection.org_id)
      const auth = this.authPayload(); form.append('token', auth.token); form.append('token_uuid', auth.token_uuid)
      this.avatarLoading = true
      try {
        const response = await axios.post(`${apiUrl}/upload/organisation/avatar/`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
        if (response.data?.success !== 1) throw new Error(response.data?.error || 'upload_failed')
        this.organisation.avatar_url = response.data.url; this.$q.notify({ color: 'positive', message: 'Изображение организации обновлено' })
      } catch { this.avatarError = 'Не удалось загрузить изображение. Повторите попытку.' }
      finally { this.avatarLoading = false; this.newAvatar = null }
    },
    async deleteAvatar() {
      this.avatarLoading = true; this.avatarError = ''
      try {
        const response = await axios.post(`${apiUrl}/upload/organisation/avatar/delete/`, { org_id: this.connection.org_id, ...this.authPayload() })
        if (response.data?.success !== 1) throw new Error(response.data?.error || 'delete_failed')
        this.organisation.avatar_url = null; this.$q.notify({ color: 'positive', message: 'Изображение организации удалено' })
      } catch { this.avatarError = 'Не удалось удалить изображение. Повторите попытку.' }
      finally { this.avatarLoading = false; this.newAvatar = null }
    }
  }
}
</script>

<style scoped>
.identity { display: flex; align-items: center; gap: var(--sid-space-5); }
.identity__content { display: flex; min-width: 0; flex-direction: column; }
.identity__content strong { font-size: var(--sid-font-size-section); overflow-wrap: anywhere; }
.identity__content > span, .readonly-note { color: var(--sid-color-text-secondary); }
.identity__actions { display: flex; flex-wrap: wrap; gap: var(--sid-space-2); margin-top: var(--sid-space-2); }
.hidden { display: none; }
.form-error { margin: var(--sid-space-3) 0 0; color: var(--sid-color-danger); }
.requisites { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-4) var(--sid-space-6); margin: 0; }
.requisites--single { grid-template-columns: 1fr; }
.requisites div { min-width: 0; }
.requisites dt { color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-label); }
.requisites dd { margin: var(--sid-space-1) 0 0; overflow-wrap: anywhere; }
@media (max-width: 599px) {
  .identity { align-items: flex-start; flex-direction: column; }
  .identity__actions { align-items: stretch; flex-direction: column; }
  .requisites { grid-template-columns: 1fr; }
}
</style>
